// Migrated 1:1 from @nexato/nx-core-module (shared/services/sidebar-wrapper.component.ts, #686)
import { ChangeDetectionStrategy, Component, inject, input, output } from '@angular/core';
import { Router } from '@angular/router';
import { SidebarService } from '../../services/sidebar/sidebar.service';

/**
 * Generic wrapper component for sidebar content.
 * Provides consistent header with label, copy-link, expand and close buttons.
 *
 * Usage:
 * ```html
 * <lib-sidebar-wrapper
 *   label="Aufgabe"
 *   [entityRoute]="['tasks', 'task', taskId]">
 *   <app-task-view [showBreadcrumb]="false" />
 * </lib-sidebar-wrapper>
 * ```
 */
@Component({
  selector: 'lib-sidebar-wrapper',
  host: { class: 'block overflow-y-auto overflow-x-hidden' },
  styles: [`
    :host {
      display: block;
    }
  `],
  template: `
    <div class="sidebar-detail-wrapper">
      <!-- Header -->
      <div class="flex flex-row border-b w-full pr-6 pl-6 py-2">
        <div class="grow text-base font-medium min-w-0 flex items-center">
          <span>{{ label() }}</span>
          <nx-default-button
            class="ml-1"
            [style]="'dark'"
            [tooltip]="'Link kopieren'"
            (click)="copyLink()"
            [icon]="'icon-copy'">
          </nx-default-button>
        </div>
        <div class="flex flex-row justify-center ml-2 shrink-0">
          <nx-default-button
            class="mr-1"
            [style]="'dark'"
            [tooltip]="'Inhalt maximieren'"
            (click)="openEntity()"
            [icon]="'icon-expand'">
          </nx-default-button>
          <nx-default-button
            [style]="'dark'"
            [tooltip]="'Schließen'"
            (click)="close()"
            [icon]="'icon-cancel'">
          </nx-default-button>
        </div>
      </div>
      <!-- Content -->
      <div class="mx-6 mt-3 mb-3">
        <ng-content />
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SidebarWrapperComponent {
  // Inputs
  /** Label shown in the sidebar header (e.g., "Aufgabe", "Vorgang") */
  readonly label = input.required<string>();

  /** Route segments to the entity detail page (e.g., ['tasks', 'task', '123']) */
  readonly entityRoute = input.required<string[]>();

  // Outputs
  /** Emitted when the entity is opened in a new view */
  readonly opened = output<void>();

  // Injected services
  private readonly sidebarService = inject(SidebarService);
  private readonly router = inject(Router);

  /**
   * Close the sidebar
   */
  close(): void {
    this.sidebarService.close();
  }

  /**
   * Navigate to the entity detail page and close sidebar
   */
  openEntity(): void {
    const route = this.entityRoute();
    if (route?.length) {
      const url = this.router.serializeUrl(
        this.router.createUrlTree(['./' + route.join('/')])
      );
      this.sidebarService.close();
      this.router.navigateByUrl(url);
      this.opened.emit();
    }
  }

  /**
   * Copy the entity URL to clipboard
   */
  copyLink(): void {
    const route = this.entityRoute();
    if (route?.length) {
      const url = this.router.serializeUrl(
        this.router.createUrlTree(['./' + route.join('/')])
      );
      const fullUrl = window.location.origin + url;
      navigator.clipboard.writeText(fullUrl);
    }
  }
}
